import React, { useEffect, useMemo, useState } from "react";
import { BadgeIndianRupee, Boxes, PackageX, ShoppingCart, Tags, Warehouse } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import TrendBars from "./components/TrendBars";
import { money, n } from "./components/reportFormat";

export default function WarehouseReports({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ReportsService.warehouses(filters)
      .then((r) => alive && setData(r.data))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters]);

  const rows = data?.rows || [];
  const stats = useMemo(() => ({
    total: rows.length,
    orders: rows.reduce((s, r) => s + Number(r.orders || 0), 0),
    revenue: rows.reduce((s, r) => s + Number(r.revenue || 0), 0),
    salesAmount: rows.reduce((s, r) => s + Number(r.sales_amount || 0), 0),
    purchaseCost: rows.reduce((s, r) => s + Number(r.purchase_cost || 0), 0),
    actualProfit: rows.reduce((s, r) => s + Number(r.actual_profit || 0), 0),
    stock: rows.reduce((s, r) => s + Number(r.stock_qty || 0), 0),
    expired: rows.reduce((s, r) => s + Number(r.expired_batches || 0), 0),
  }), [rows]);

  const trendRows = rows.map((r, i) => ({ bucket: new Date(Date.now() + i * 86400000).toISOString(), actual_profit: Number(r.actual_profit || 0), revenue: Number(r.revenue || 0), name: r.name }));

  if (loading) return <div className="py-14 text-center font-bold text-[#d86a78]">Loading warehouse report...</div>;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={Warehouse} title="Warehouses" value={n(stats.total)} />
        <ReportCard icon={ShoppingCart} title="Orders" value={n(stats.orders)} />
        <ReportCard icon={BadgeIndianRupee} title="Revenue" value={money(stats.revenue)} />
        <ReportCard icon={BadgeIndianRupee} title="Purchase Cost" value={money(stats.purchaseCost)} />
        <ReportCard icon={Tags} title="Actual Profit" value={money(stats.actualProfit)} />
        <ReportCard icon={Boxes} title="Stock Qty" value={n(stats.stock)} />
        <ReportCard icon={PackageX} title="Expired Batches" value={n(stats.expired)} />
      </div>

      <TrendBars rows={trendRows} field="actual_profit" label="Warehouse actual profit trend" subtitle="Profit = item selling amount minus purchase cost" />

      <ReportPanel>
        <DataTable rows={rows} columns={["name","code","city","state","is_active","orders","delivered_orders","revenue","sales_amount","purchase_cost","actual_profit","avg_purchase_price","profit_margin_percent","missing_purchase_price_items","stock_qty","stock_products","expired_batches"]} />
      </ReportPanel>
    </div>
  );
}
